import {uniq} from "lodash";
import {IconName} from "@reside/ui";

import {UserPermissionEnum} from "../../../utils/types/user";
import {ChecklistItem} from "../../../services/ReportingService";
import {
  Section,
  reportingCompletionTime,
  reportingMenuGeneral,
  reportingMenuOther,
  reportingOverview,
  sectionToMenuItem,
} from "../constants";
import {getSectionItemsPermissions} from "../permissions.helpers";
import {CHECKLIST_ITEM_REPORT_ID} from "../reports";
import {QuicksightDashboard} from "../../../services/QuicksightService";
import {DASHBOARD_REPORT_ID} from "../reports/ReportQuicksightDashboard";
import {WorkflowType} from "../../../utils/enums";
import {
  getWorkflowLabel,
  resolveWorkflowType,
} from "../../../atoms/start-workflow/workflowMapping";

/**
 * Existing URL bucket for list/report routes. Kept even though the menu is now
 * grouped by workflow, because routing (`ReportingRouter`) and path helpers
 * (`menuSectionPath`) still key off these segments.
 */
export type ReportingListCategory = "general" | "other" | "checkboxes";

export const CHECKBOXES_URL_COMPONENT = "checkboxes";

export type MenuItem = Readonly<{
  label: string;
  icon?: IconName;
  urlComponent: string;
  children?: ReadonlyArray<MenuItem>;
  permissions?: ReadonlyArray<UserPermissionEnum>;
  sectionItems?: ReadonlyArray<string>;
  isNavigationEnabled?: boolean;
  /** Canonical workflow key for grouping (see `buildReportingWorkflowMenu`). */
  workflowType?: string;
  /** URL bucket used to build the section path; undefined for group nodes. */
  listCategory?: ReportingListCategory;
}>;

/**
 * Preferred root order when those types are in the facility union. Remaining
 * scoped types follow alphabetically.
 */
export const REPORTING_WORKFLOW_ORDER: ReadonlyArray<string> = [
  WorkflowType.ADMISSION,
  WorkflowType.ARBITRATION,
];

export type ReportingSectionMeta = Readonly<{
  sectionId: string;
  label: string;
  workflowType: string;
  listCategory: ReportingListCategory;
}>;

const staticReportingSectionMeta = new Map<string, ReportingSectionMeta>(
  [...reportingMenuGeneral, ...reportingMenuOther].map(item => [
    item.urlComponent,
    {
      sectionId: item.urlComponent,
      label: item.label,
      workflowType: item.workflowType ?? WorkflowType.ADMISSION,
      listCategory: item.listCategory ?? "general",
    },
  ]),
);

/**
 * Resolve workflow/label metadata for a section id. Checklist (checkboxes)
 * items are dynamic; pass `checklistItems` when resolving checkbox routes.
 * Overview / Completion Time ids are parsed (admission keeps legacy slugs).
 */
const OVERVIEW_ID_SUFFIX = "-overview";
const COMPLETION_TIME_ID_SUFFIX = "-completion-time";

export type WorkflowSharedSectionKind = "overview" | "completion-time";

export const workflowOverviewSectionId = (workflowKey: string): string => {
  const key = resolveWorkflowType(workflowKey);

  return key === WorkflowType.ADMISSION
    ? reportingOverview.id
    : `${key}${OVERVIEW_ID_SUFFIX}`;
};

export const workflowCompletionTimeSectionId = (workflowKey: string): string => {
  const key = resolveWorkflowType(workflowKey);

  return key === WorkflowType.ADMISSION
    ? reportingCompletionTime.id
    : `${key}${COMPLETION_TIME_ID_SUFFIX}`;
};

export const parseWorkflowSharedSectionId = (
  sectionId: string,
):
  | Readonly<{kind: WorkflowSharedSectionKind; workflowType: string}>
  | undefined => {
  if (sectionId === reportingOverview.id) {
    return {kind: "overview", workflowType: WorkflowType.ADMISSION};
  }

  if (sectionId === reportingCompletionTime.id) {
    return {kind: "completion-time", workflowType: WorkflowType.ADMISSION};
  }

  if (sectionId.endsWith(COMPLETION_TIME_ID_SUFFIX)) {
    const workflowType = sectionId.slice(0, -COMPLETION_TIME_ID_SUFFIX.length);

    return workflowType
      ? {kind: "completion-time", workflowType: resolveWorkflowType(workflowType)}
      : undefined;
  }

  if (sectionId.endsWith(OVERVIEW_ID_SUFFIX)) {
    const workflowType = sectionId.slice(0, -OVERVIEW_ID_SUFFIX.length);

    return workflowType
      ? {kind: "overview", workflowType: resolveWorkflowType(workflowType)}
      : undefined;
  }

  return undefined;
};

export const createWorkflowOverviewSection = (workflowKey: string): Section => ({
  id: workflowOverviewSectionId(workflowKey),
  title: reportingOverview.title,
  sectionItems: reportingOverview.sectionItems,
  workflowType: resolveWorkflowType(workflowKey),
});

export const createWorkflowCompletionTimeSection = (workflowKey: string): Section => ({
  id: workflowCompletionTimeSectionId(workflowKey),
  title: reportingCompletionTime.title,
  sectionItems: reportingCompletionTime.sectionItems,
  workflowType: resolveWorkflowType(workflowKey),
});

export const getReportingSectionMeta = (
  sectionId: string,
  listCategory: ReportingListCategory,
  checklistItems?: ReadonlyArray<ChecklistItem>,
): ReportingSectionMeta | undefined => {
  if (listCategory === CHECKBOXES_URL_COMPONENT) {
    const item = checklistItems?.find(
      checklistItem => checklistItem.id === sectionId,
    );

    return {
      sectionId,
      label: item?.description ?? sectionId,
      workflowType: resolveWorkflowType(item?.workflowType),
      listCategory: CHECKBOXES_URL_COMPONENT,
    };
  }

  const sharedSection = parseWorkflowSharedSectionId(sectionId);

  if (sharedSection) {
    return {
      sectionId,
      label:
        sharedSection.kind === "overview"
          ? reportingOverview.title
          : reportingCompletionTime.title,
      workflowType: sharedSection.workflowType,
      listCategory: "general",
    };
  }

  return staticReportingSectionMeta.get(sectionId);
};

export const getMenuItemPermissions = (
  menuItem: MenuItem,
): UserPermissionEnum[] =>
  uniq([
    ...(menuItem.children ?? []).reduce(
      (permissions, childMenuItem) => [
        ...permissions,
        ...getMenuItemPermissions(childMenuItem),
      ],
      [],
    ),
    ...getSectionItemsPermissions(menuItem.sectionItems || []),
  ]);

export type MenuItemAccess = "check" | "allow" | "hide";

/**
 * Empty `permissions` from RequiredUserPermissionsSome is "no access"
 * (`[].some()`). Dashboard items have no Reside permission on the read path,
 * so treat empty requirements as visible when the node has content.
 */
export const getMenuItemAccess = (
  menuItem: MenuItem,
  permissions: UserPermissionEnum[] = getMenuItemPermissions(menuItem),
): MenuItemAccess => {
  if (permissions.length > 0) {
    return "check";
  }

  const hasContent =
    (menuItem.children?.length ?? 0) > 0 ||
    (menuItem.sectionItems?.length ?? 0) > 0;

  return hasContent ? "allow" : "hide";
};

export const getMenuItemSections = (item: MenuItem): string[] => [
  ...(item.sectionItems || []),
  ...(item.children || []).flatMap(getMenuItemSections),
];

const filterByWorkflow = (
  items: ReadonlyArray<MenuItem>,
  workflowKey: string,
): MenuItem[] =>
  items.filter(item => resolveWorkflowType(item.workflowType) === workflowKey);

const orderScopedWorkflowKeys = (
  scopedWorkflowTypes: ReadonlyArray<string>,
): ReadonlyArray<string> => {
  const resolved = uniq(
    scopedWorkflowTypes.map(workflowType => resolveWorkflowType(workflowType)),
  );
  const preferred = REPORTING_WORKFLOW_ORDER.filter(key =>
    resolved.includes(key),
  );
  const rest = resolved
    .filter(key => !REPORTING_WORKFLOW_ORDER.includes(key))
    .sort();

  return [...preferred, ...rest];
};

/**
 * Build the workflow-grouped reporting tree from the facility-scoped union.
 * Each root gets Workflow Overview + Completion Time, then static sections
 * tagged for that workflow, then Checkboxes when the checklist API returned
 * items. Checklist-only types do not create roots.
 */
export const buildReportingWorkflowMenu = (
  checkboxMenu: ReadonlyArray<MenuItem>,
  scopedWorkflowTypes: ReadonlyArray<string> = [],
): ReadonlyArray<MenuItem> =>
  orderScopedWorkflowKeys(scopedWorkflowTypes).map(workflowKey => {
    const children: MenuItem[] = [
      sectionToMenuItem(createWorkflowOverviewSection(workflowKey), "general"),
      sectionToMenuItem(
        createWorkflowCompletionTimeSection(workflowKey),
        "general",
      ),
      ...filterByWorkflow(reportingMenuGeneral, workflowKey),
      ...filterByWorkflow(reportingMenuOther, workflowKey),
    ];

    const workflowCheckboxes = filterByWorkflow(checkboxMenu, workflowKey);

    if (workflowCheckboxes.length > 0) {
      children.push({
        label: "Checkboxes",
        urlComponent: CHECKBOXES_URL_COMPONENT,
        children: workflowCheckboxes,
      });
    }

    return {
      label: getWorkflowLabel(workflowKey),
      urlComponent: workflowKey,
      workflowType: workflowKey,
      children,
    };
  });

/**
 * Create reporting menu definition with dynamic checkbox menu.
 */
export const getMenuDefinition = (
  checkboxMenu: ReadonlyArray<MenuItem>,
  dashboardMenu: ReadonlyArray<MenuItem>,
  scopedWorkflowTypes: ReadonlyArray<string> = [],
): ReadonlyArray<MenuItem> => [
  {
    label: "General Reporting",
    urlComponent: "reporting",
    children: buildReportingWorkflowMenu(checkboxMenu, scopedWorkflowTypes),
  },
  ...(dashboardMenu.length > 0
    ? [
        {
          label: "Business Reporting",
          urlComponent: "analytics",
          children: [
            {
              label: "Analytics & Insights",
              urlComponent: "dashboards",
              children: dashboardMenu,
            },
          ],
        },
      ]
    : []),
];

export const mapDashboardsToMenuDefinition = (
  dashboards: ReadonlyArray<QuicksightDashboard>,
) =>
  dashboards.map(({id, name}) => ({
    label: name,
    urlComponent: id,
    // Keep sectionItems for active-state matching; do not map to
    // QUICKSIGHT_DASHBOARDS — visibility comes from GET /dashboards.
    sectionItems: [DASHBOARD_REPORT_ID],
  }));

export const mapChecklistItemToMenuDefinition = (
  checklistItems: ReadonlyArray<ChecklistItem>,
) =>
  checklistItems
    .map<Section>(({id, description, workflowType}) => ({
      id,
      title: description,
      sectionItems: [CHECKLIST_ITEM_REPORT_ID],
      workflowType: workflowType ?? WorkflowType.ADMISSION,
    }))
    .map(section => sectionToMenuItem(section, CHECKBOXES_URL_COMPONENT));
